import { CSSProperties, FC, useState } from "react";
import { Collapse, Space, Table, Tooltip } from "antd";
import { EyeFilled } from "@ant-design/icons";
import { SvgIcon } from "../../../../common/components/SvgIcon";
import { getScoreGrade } from "../../../../common/Grade";
import { Leverage } from "../../../../types/IntelligenceTypes";
import classes from "./LeverageSummary.module.less";

const { Panel } = Collapse;
const { Column } = Table;
const PANEL_KEY = "singlePanel";

interface LeverageSummaryProps {
    leverage?: Leverage;
    defaultActive?: boolean;
    style?: CSSProperties;
    showButtons?: boolean;
}

export const LeverageSummary: FC<LeverageSummaryProps> = ({
    leverage,
    defaultActive,
    style,
    showButtons = true,
}) => {
    const [activeKey, setActiveKey] = useState<string[]>(defaultActive ? [PANEL_KEY] : []);

    if (!leverage) return null;

    const score = leverage.score * 10;
    const gradeInfo = getScoreGrade(score);
    const scoreClass = classes[`container-${gradeInfo.tag}`];

    return (
        <Collapse
            className={`${classes.container} ${scoreClass}`}
            expandIconPosition="end"
            expandIcon={() => <SvgIcon name="down" size={20} className={classes.expandIcon} />}
            activeKey={activeKey}
            onChange={(value) => setActiveKey(value as string[])}
            style={style}
        >
            <Panel
                key={PANEL_KEY}
                className={classes.main}
                header={
                    <div className={classes.header}>
                        <div className={classes.scoreBox}>
                            <Space size={8}>
                                <span className={classes.title}>Negotiation leverage</span>
                                <Tooltip title="The Negotiation Leverage Score is a metric that quantifies the relative strength of your position in a negotiation. It helps you understand how much influence you have and how likely you are to achieve your desired outcomes.">
                                    <EyeFilled className={classes.eye} />
                                </Tooltip>
                            </Space>
                            <span className={classes.score}>{score}%</span>
                        </div>
                        <div>
                            <div className={classes.title}>Key points</div>
                            <ul>
                                {leverage.summary?.map((item) => {
                                    if (typeof item === "string") return <li key={item}>{item}</li>;
                                    return (
                                        <li key={item.name}>
                                            <span className={classes.emphasis}>{item.name}: </span>
                                            {item.description}
                                        </li>
                                    );
                                })}
                            </ul>
                            {showButtons ? (
                                <div className={classes.showDetail}>Show details</div>
                            ) : null}
                        </div>
                    </div>
                }
            >
                <div className={classes.content}>
                    {leverage.constraints ? (
                        <>
                            <div className={classes.subTitle}>Constraints limiting leverage</div>
                            <ul>
                                {leverage.constraints.map((item) => (
                                    <li key={item.name}>
                                        <span className={classes.emphasis}>{item.name}: </span>
                                        {item.description}
                                    </li>
                                ))}
                            </ul>
                        </>
                    ) : null}
                    {leverage.recommendations ? (
                        <>
                            <div className={classes.subTitle}>
                                Tactical recommendations to tilt the field
                            </div>
                            <Table
                                pagination={false}
                                dataSource={leverage.recommendations}
                                size="small"
                            >
                                <Column
                                    title="Play"
                                    dataIndex="play"
                                    width={334}
                                    render={(value) => (
                                        <span className={classes.emphasis}>{value}</span>
                                    )}
                                />
                                <Column title="Why it works" dataIndex="why" width={334} />
                                <Column title="How to execute" dataIndex="how" />
                            </Table>
                        </>
                    ) : null}
                    {leverage.bottomLine ? (
                        <>
                            <div className={classes.subTitle}>Bottom line</div>
                            <div>{leverage.bottomLine}</div>
                        </>
                    ) : null}
                    {showButtons ? (
                        <div className={classes.hideDetail} onClick={() => setActiveKey([])}>
                            Hide details
                        </div>
                    ) : null}
                </div>
            </Panel>
        </Collapse>
    );
};
